import { isValidElement, type ReactNode } from "react";

import type { SnackbarOrigin } from "@mui/material";

export type ToastSeverity = "success" | "error" | "warning" | "info";

export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

const DEFAULT_ANCHOR_ORIGIN: SnackbarOrigin = {
  vertical: "bottom",
  horizontal: "center",
};

function anchorOriginForPosition(position?: ToastPosition): SnackbarOrigin {
  if (!position) {
    return DEFAULT_ANCHOR_ORIGIN;
  }
  const [vertical, horizontal] = position.split("-") as [
    SnackbarOrigin["vertical"],
    SnackbarOrigin["horizontal"],
  ];
  return { vertical, horizontal };
}

export interface ToastOptions {
  autoClose?: number | false;
  position?: ToastPosition;
  /**
   * Suppresses queuing a toast while one with the same id is already
   * showing or waiting. Unlike react-toastify, a matching id does not
   * update the existing toast in place — later calls are just dropped.
   */
  toastId?: string;
  /** Pauses the auto-dismiss timer while the pointer is over the toast. Defaults to `true`. */
  pauseOnHover?: boolean;
  /** Dismisses the toast when its body (not just the close button) is clicked. Defaults to `true`. */
  closeOnClick?: boolean;
  /**
   * Accepted for react-toastify call-site compatibility; no effect here —
   * this implementation renders no progress-bar UI and has no
   * drag-to-dismiss gesture.
   */
  hideProgressBar?: boolean;
  draggable?: boolean;
}

export interface QueuedToast {
  key: number;
  toastId?: string;
  severity: ToastSeverity;
  message: ReactNode;
  autoClose: number | false;
  anchorOrigin: SnackbarOrigin;
  pauseOnHover: boolean;
  closeOnClick: boolean;
}

function isSafeReactNode(value: unknown): value is ReactNode {
  if (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (isValidElement(value)) {
    return true;
  }
  return Array.isArray(value) && value.every(isSafeReactNode);
}

/**
 * Callers sometimes pass a caught error straight through (`toast.error(err)`).
 * Rendering a non-ReactNode value as a child throws, which would take down
 * the host, so anything that isn't already safe to render is normalized to
 * a string.
 */
function toDisplayMessage(message: unknown): ReactNode {
  if (isSafeReactNode(message)) {
    return message;
  }
  if (message instanceof Error) {
    return message.message || message.name;
  }
  try {
    return String(message);
  } catch {
    return "An unexpected error occurred.";
  }
}

export interface ToastState {
  queue: QueuedToast[];
  open: boolean;
}

let state: ToastState = { queue: [], open: false };
let nextKey = 0;
const listeners = new Set<() => void>();

function setState(patch: Partial<ToastState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function enqueue(
  severity: ToastSeverity,
  message: ReactNode,
  options?: ToastOptions,
) {
  if (
    options?.toastId !== undefined &&
    state.queue.some((item) => item.toastId === options.toastId)
  ) {
    return;
  }

  const item: QueuedToast = {
    key: nextKey++,
    toastId: options?.toastId,
    severity,
    message: toDisplayMessage(message),
    autoClose: options?.autoClose ?? 3000,
    anchorOrigin: anchorOriginForPosition(options?.position),
    pauseOnHover: options?.pauseOnHover ?? true,
    closeOnClick: options?.closeOnClick ?? true,
  };

  if (state.queue.length === 0) {
    setState({ queue: [item], open: true });
  } else {
    setState({ queue: [...state.queue, item] });
  }
}

export function closeCurrentToast() {
  setState({ open: false });
}

export function advanceToastQueue() {
  const queue = state.queue.slice(1);
  setState({ queue, open: queue.length > 0 });
}

export function subscribeToToastState(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToastStateSnapshot() {
  return state;
}

export function resetToastState() {
  state = { queue: [], open: false };
}

export interface Toast {
  (message: ReactNode, options?: ToastOptions): void;
  success: (message: ReactNode, options?: ToastOptions) => void;
  error: (message: ReactNode, options?: ToastOptions) => void;
  warning: (message: ReactNode, options?: ToastOptions) => void;
  warn: (message: ReactNode, options?: ToastOptions) => void;
  info: (message: ReactNode, options?: ToastOptions) => void;
  dismiss: () => void;
  clearWaitingQueue: () => void;
}

const toastFn = ((message: ReactNode, options?: ToastOptions) =>
  enqueue("info", message, options)) as Toast;

toastFn.success = (message, options) => enqueue("success", message, options);
toastFn.error = (message, options) => enqueue("error", message, options);
toastFn.warning = (message, options) => enqueue("warning", message, options);
toastFn.warn = (message, options) => enqueue("warning", message, options);
toastFn.info = (message, options) => enqueue("info", message, options);
toastFn.dismiss = () => closeCurrentToast();
toastFn.clearWaitingQueue = () => setState({ queue: state.queue.slice(0, 1) });

export const toast = toastFn;
